import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Camera, Eraser, CheckCircle2 } from "lucide-react";

function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FBF7F1";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#1F4032";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * (c.width / rect.width), y: y * (c.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FBF7F1";
    ctx.fillRect(0, 0, c.width, c.height);
    onChange(null);
  };

  return (
    <div>
      <div className="border border-[#1F4032]/15 rounded-md overflow-hidden bg-[#FBF7F1]">
        <canvas
          ref={canvasRef}
          width={600} height={200}
          className="w-full h-44 touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-muted-foreground">Signez avec la souris ou le doigt</span>
        <button onClick={clear} type="button" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-[#1F4032]" data-testid="signature-clear">
          <Eraser className="w-3 h-3" /> Effacer
        </button>
      </div>
    </div>
  );
}

export default function ProofOfDeliveryDialog({ open, onOpenChange, onConfirm, loading }) {
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [signedName, setSignedName] = useState("");
  const [reserves, setReserves] = useState("Aucune réserve");

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setPhoto(null); setSignature(null); setSignedName(""); setReserves("Aucune réserve");
  };

  useEffect(() => { if (!open) reset(); }, [open]);

  const ready = photo && signature && signedName.trim().length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="proof-dialog">
        <DialogHeader>
          <DialogTitle>Justificatif de livraison</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Pour confirmer la livraison, fournissez une photo de la marchandise déposée et la signature du destinataire.
          </p>
        </DialogHeader>

        <div className="space-y-5">
          {/* Photo */}
          <div>
            <Label className="overline">Photo de la marchandise</Label>
            <div className="mt-1">
              {photo ? (
                <div className="relative border border-[#1F4032]/15 rounded-md overflow-hidden">
                  <img src={photo} alt="Preuve" className="w-full max-h-56 object-contain bg-neutral-50" />
                  <button onClick={() => setPhoto(null)} className="absolute top-2 right-2 bg-white/90 text-xs px-2 py-1 rounded-md" data-testid="photo-reset">
                    Reprendre
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer flex items-center justify-center gap-2 h-32 border-2 border-dashed border-[#1F4032]/20 rounded-md hover:bg-[#1F4032]/5" data-testid="photo-upload-label">
                  <Camera className="w-5 h-5 text-[#1F4032]" />
                  <span className="text-sm">Prendre une photo / choisir un fichier</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="photo-upload" />
                </label>
              )}
            </div>
          </div>

          {/* Signature */}
          <div>
            <Label className="overline">Signature du destinataire</Label>
            <div className="mt-1">
              <SignaturePad value={signature} onChange={setSignature} />
            </div>
          </div>

          {/* Nom du signataire */}
          <div>
            <Label className="overline">Nom du destinataire</Label>
            <Input value={signedName} onChange={(e) => setSignedName(e.target.value)}
              placeholder="Nom et prénom" className="mt-1 h-11"
              data-testid="signed-name" />
          </div>

          {/* Réserves : « Aucune réserve » est la valeur explicite par défaut. */}
          <div>
            <Label className="overline">Réserves éventuelles</Label>
            <Input value={reserves} onChange={(e) => setReserves(e.target.value)}
              placeholder="Aucune réserve" className="mt-1 h-11"
              data-testid="proof-reserves" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            disabled={!ready || loading}
            onClick={() => onConfirm({ proof_photo: photo, proof_signature: signature, proof_signed_name: signedName, proof_reserves: reserves.trim() || "Aucune réserve" })}
            className="bg-[#1F4032] hover:bg-[#2A5443] gap-2"
            data-testid="proof-confirm">
            <CheckCircle2 className="w-4 h-4" />
            {loading ? "Confirmation…" : "Confirmer la livraison"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
